import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImage } from "@/lib/api";
import SEO from "@/components/SEO";

/** Blog index — lists all published articles */
export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="mx-auto max-w-[1200px] px-5 sm:px-10 py-14 sm:py-20" data-testid="blog-index">
      <SEO
        title="Journal — Style Guides & Gift Ideas"
        description="Style guides, gift edits and jewellery care tips from Star Jewels. Learn how to style layered necklaces, discover gift ideas under ₹2,000 and more."
        path="/blog"
      />
      <div className="overline">Journal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-light mt-2 leading-tight text-sj-ink">
        Stories & style guides
      </h1>
      <p className="mt-3 text-sm text-sj-inkSoft max-w-lg">
        Notes on styling, gifting, and everyday elegance — hand-picked by the Star Jewels team.
      </p>

      <section className="mt-12">
        {loading ? (
          <div className="text-sm text-sj-muted">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-10 rounded-md bg-white border border-dashed border-sj-ink/15 text-sm text-sj-inkSoft text-center">
            First article coming soon. Check back with us.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p) => (
              <article key={p.id} className="group" data-testid={`blog-card-${p.slug}`}>
                <Link to={`/blog/${p.slug}`} className="block">
                  <div className="aspect-[4/3] bg-white overflow-hidden rounded-md border border-sj-ink/8">
                    {p.cover_image ? (
                      <img
                        src={resolveImage(p.cover_image, { w: 900 })}
                        alt={`${p.title} — Star Jewels blog`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-sj-cream text-sj-muted text-xs">
                        Star Jewels Journal
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-sj-goldDark">
                      {new Date(p.published_at || p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    <h2 className="mt-2 font-display text-xl text-sj-ink group-hover:text-sj-goldDark transition-colors leading-tight">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-2 text-sm text-sj-inkSoft line-clamp-3">{p.excerpt}</p>
                    )}
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
